// ─────────────────────────────────────────────────────────────────────────────
// ip_checkout.ts — Pricing helper (PHP backend — MySQL source of truth)
// Conecta con endpoint PHP del VPS para leer precios de ip_products.
//
// Endpoint PHP (VPS):
//   GET  https://imagenprinting.com/api/products/pricing.php?slug=xxx&quantity=N
//
// NOTA: El pago usa la infraestructura EXISTENTE del Kiosk:
//   - square-kiosk-link edge function (Square Payment Link)
//   - paypal-kiosk-link + paypal-kiosk-capture (PayPal)
// CERO edge functions nuevas. CERO endpoints nuevos.
// ─────────────────────────────────────────────────────────────────────────────

const VPS_API_BASE = 'https://imagenprinting.com/api';

// ── Pricing types ──
export interface ProductPricingData {
  family: {
    id: string;
    name: string;
    slug: string;
    displayName: string;
    starting_price_cents: number;
    productType: string;
  };
  products: Array<{
    id: number;
    name: string;
    sku: string | null;
    price: number;
    kind: string;
  }>;
  pricing: {
    quantity: number;
    front_only: { price: number; product_id: number } | null;
    front_back: { price: number; product_id: number } | null;
  };
  promotions: Array<{
    id: number;
    title_es: string;
    price: string;
    badge: string | null;
    product_id: number;
    expires_at: string | null;
    desc_es?: string;
  }>;
  designService: {
    price: number;
    label_es: string;
    description: string;
  } | null;
  design: {
    serviceFee: number;
    allowUpload: boolean;
    allowService: boolean;
    allowReview: boolean;
    allowBackDesign: boolean;
  };
  printTypes: Array<{ value: string; label_es: string }>;
  flow: {
    steps: Array<any>;
    quantities: Array<any>;
    materials: Array<any>;
    finishes: Array<any>;
    production: Array<any>;
    quantityLabel: string;
    allowCustomQuantity: boolean;
  };
}

/**
 * Obtiene precios de productos desde el backend PHP (MySQL — única fuente de verdad).
 * El PHP lee ip_products, ip_promotions, ip_categories directamente.
 */
export async function fetchProductPricing(
  slug: string,
  quantity: number = 1000
): Promise<{ success: boolean; data?: ProductPricingData; error?: string }> {
  const params = new URLSearchParams({ slug, quantity: String(quantity) });
  const url = `${VPS_API_BASE}/products/pricing.php?${params.toString()}`;

  try {
    const res = await fetch(url, { method: 'GET' });
    const raw = await res.text();

    let data: any;
    try { data = JSON.parse(raw); } catch {
      return { success: false, error: `PHP returned non-JSON: ${raw.slice(0, 200)}` };
    }

    if (!res.ok || !data.success) {
      return { success: false, error: data.error || data.message || `HTTP ${res.status}` };
    }

    return { success: true, data: data.data || data };
  } catch (err: any) {
    const isNet = err instanceof TypeError && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'));
    return {
      success: false,
      error: isNet
        ? 'El servicio de precios no está disponible desde este preview. Funcionará en imagenprinting.com.'
        : err.message || 'Error de conexión',
    };
  }
}